import { DefaultOptionType } from "antd/lib/select";
import { EWorkflowStatusDataBlock } from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import type { FilterOptions } from "components/schedule-hierarchy/schedule-filter-bar";

export type TFeatureName =
  | "MATERIALS"
  | "SUBMITTALS"
  | "DESIGN_PACKAGE"
  | "BID_PACKAGE"
  | "SCHEDULES";

export type TFeatureDetail = {
  featureTypeId: FeatureTypes;
  feature: TFeatureName;
  title: string;
  titlePlural: string;
};

export type TSpecSection = {
  title: string | null;
  section_number: string | null;
};

export type TFeatureSpecSection = {
  spec_section: TSpecSection;
};
export type DropDownDBFeatureInstance = {
  id: string;
  title: string;
  auto_inc_value: number;
  feature_instance_spec_sections: TFeatureSpecSection[];
  feature_submittals: [{ material_tracking: boolean }];
  links_as_source_aggregate: {
    aggregate: {
      count: number;
    };
  };
  links_as_target_aggregate: {
    aggregate: {
      count: number;
    };
  };
  successor_material_links?: {
    aggregate: {
      count: number;
    };
  };
  predecessor_material_links?: {
    aggregate: {
      count: number;
    };
  };
};

export type DropdownEntityOption = {
  isSuggested: boolean;
  pre: string;
  title: string;
  label: string;
  id: string;
  isLinked: boolean;
  instance: DropDownDBFeatureInstance;
};

export type TFeatureActivity = {
  type: number;
  duration: number;
  end_date: string;
  start_date: string;
  import_log_id: string;
  parent: string;
  critical: boolean;
  total_slack: number;
  total_slack_computed: string;
  progress: number;
  actual_start_date: null | string;
  actual_finish_date: null | string;
};

export type TFeatureInstance = {
  id: string;
  number: string;
  auto_inc_value: number;
  title: string;
  deleted_from_source: boolean;
  implicit: boolean;
  feature: {
    id: number;
  };
  feature_workflow_instance: {
    workflow_status: string;
  };
  feature_instance_spec_sections: TFeatureSpecSection[];
  feature_activities: null | TFeatureActivity[];
  feature_submittals: { material_tracking: boolean }[];
  links: TLinkSource[];
  meta?: any;
};

export type TLinkSource = {
  governing_link: boolean;
  created_at: string;
  link_type: ELinkType;
  offset_duration: null | number;
  linked_offset_date: null | string;
  reverse_dependency: boolean;
  recommended_by_system?: boolean;
  isLinkedWithStartdate: boolean;

  isSuggested?: boolean;

  featureInstanceId: string;
  feature_instance: TFeatureInstance | null | undefined;
};

export type TEntityLink = {
  id: string;
  ids: string[];
  auto_inc_value: number;
  title: string;
  feature_type_id: FeatureTypes;
  feature_instance_spec_sections: TFeatureSpecSection[];
  feature_workflow_instance: {
    workflow_status: EWorkflowStatusDataBlock;
  };
  feature_submittals: { material_tracking: boolean }[];
  links: TLinkSource[];
  predecessor_material_links?: TLinkSource[];
  successor_material_links?: TLinkSource[];
};

export type TFeatureActivityWithInstance = {
  feature_instance_id: string;
  is_suggested?: boolean;
  feature_instance: {
    id: string;
    title: string;
    number: string;
  };
} & TFeatureActivity;

export type TEnrichedActivity = TFeatureActivityWithInstance & {
  phase?: string;
  csi_division?: string;
  activity_type?: string;
  location_tags?: string;
};

export type TUnlinkedItem = {
  item: TLinkSource;
  parentMaterialId?: string;
};

export const AI_LINK_TAB_STATE = {
  OPENED_BEFORE_LINKING: "tab opened before linking",
  OPENED_AFTER_LINKING: "tab opened after linking",
  NO_TAB_OPEN: "no tab open"
} as const;

export type TAiLinkTabState =
  (typeof AI_LINK_TAB_STATE)[keyof typeof AI_LINK_TAB_STATE];

export type TAiLinkFeedbackItem = {
  activityId: string | null;
  materialId: string;
  tabState: TAiLinkTabState;
};

export type TAiRecommenderFeedback = {
  hasOpenedAiTab: boolean;
  items: TAiLinkFeedbackItem[];
};

export type TMaterialLinkDirection = "predecessor" | "successor";

export type TEditingEntity = {
  list: TLinkSource[];
  unlinkedItems: TUnlinkedItem[];
  featureDetail: TFeatureDetail;
  /** Direction chosen from the material Add menu. Used when saving material-to-material links. */
  materialLinkDirection?: TMaterialLinkDirection;
  entityDetail: {
    id: string;
    auto_inc_value: number;
    title: string;
    specSection: TSpecSection;
  };
  parentEntity: {
    link: TLinkSource;
    rightFeatureDetail: TFeatureDetail;
  } | null;
  height: number;
  aiRecommenderFeedback?: TAiRecommenderFeedback;
};

export type TActivityHookInfo = {
  activities: TEnrichedActivity[];
  isCSVFile: boolean;
  hasActive: boolean;
  loading: boolean;
  createActiveSchedule: (schedule_Id: string) => void;
  selectedScheduleId: string | undefined;
  scheduleVersionsOptions: DefaultOptionType[] | undefined;
  refetchScheduleVersions: () => void;
  refetchActivityList?: () => Promise<unknown> | void;
  filterOptions?: FilterOptions;
  hasCompletedRecommenderJob?: boolean;
  isRecommenderJobPending?: boolean;
  hasRecommenderJobError?: boolean;
  inProgressMaterialIds?: Set<string>;
  recommenderJobId?: string;
};

export enum ELinkType {
  FINISH_TO_START,
  START_TO_START,
  FINISH_TO_FINISH,
  START_TO_FINISH
}
